/**
 * Changelog ID: TCC-UTIL-001
 * Author: Cascade AI
 * Date: 2025-05-27
 * Summary: Created utility functions for loading YAML content files
 * Following WindSurf Global Rules for structured, testable code
 *
 * Changelog ID: ACC-034
 * Author: Cascade AI
 * Date: 2025-07-07
 * Summary: Added utility functions for dynamically importing JSON files from directories
 */

import yaml from 'js-yaml';

/**
 * Load YAML content from a file path
 * Used for static content in the application
 * 
 * @param {string} filePath - Path to the YAML file
 * @returns {Promise<any>} - Parsed YAML content
 */
export const loadYamlFromFile = async (filePath: string): Promise<any> => {
  try {
    // In browser environments, we need to use fetch to get the file
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to load YAML file: ${filePath}`);
    }
    
    const yamlText = await response.text();
    return yaml.load(yamlText);
  } catch (error) {
    console.error(`Error loading YAML file ${filePath}:`, error);
    throw error;
  }
};

/**
 * Load YAML content from a string
 * Useful for testing and direct string parsing
 * 
 * @param {string} yamlString - YAML content as string
 * @returns {any} - Parsed YAML content
 */
export const parseYamlString = (yamlString: string): any => {
  try {
    return yaml.load(yamlString);
  } catch (error) {
    console.error('Error parsing YAML string:', error);
    throw error;
  }
};

/**
 * Interface for Webpack's require.context
 * Provides TypeScript support for webpack's dynamic imports
 */
interface RequireContext {
  keys(): string[];
  (id: string): any;
}

/**
 * Import all JSON files from a specific context directory
 * Used for dynamic loading of products
 * 
 * Changelog ID: ACC-034
 * Author: Cascade AI
 * Date: 2025-07-07
 *
 * @returns {Record<string, any>} - Object with all imported JSON modules
 */
export const importAllJsonFromContext = (context: RequireContext): Record<string, any> => {
  const keys = context.keys();
  const modules: Record<string, any> = {};
  
  // Process each file in the context
  keys.forEach((key: string) => {
    // Remove the leading './' and file extension for the module name
    const moduleName = key.replace(/^\.\//, '').replace(/\.json$/, '');
    modules[moduleName] = context(key);
  });
  
  return modules;
};

/**
 * Parse a JSON string into an object
 * Utility function for testing and direct string parsing
 * 
 * Changelog ID: ACC-034
 * Author: Cascade AI
 * Date: 2025-07-07
 *
 * @param {string} jsonString - JSON content as string
 * @returns {any} - Parsed JSON content
 */
export const parseJsonString = (jsonString: string): any => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error parsing JSON string:', error);
    throw error;
  }
};
